import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location, NgIf } from '@angular/common';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { CdkScrollable } from '@angular/cdk/overlay';
import { MatDialog } from '@angular/material/dialog'; // TO-DO
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';

import { StepClickedEvent } from '../../../shared/components/stepper/stepper.component';
import { LocationService } from '../../../shared/services/location.service';
import { FastLaneWizardService } from '../../services/fastlane-wizard.service';
import { FastLaneService } from '../../services/fastlane.service';
import { FastLaneWizardStep } from '../../models/fastlane-wizard-state';
import { TranslationService } from '../../../shared/services/translation.service';
import { UserService } from '../../../shared/services/user.service';
import { FormFastLane } from '../../models/form-fastlane';
import { FastLaneMainContent } from '../../../shared/models/settings';
import { ErrorService } from '../../../shared/services/error.service';
import { SubmitDialogComponent } from '../../../shared/components/submit-dialog/submit-dialog.component';
import { AfterViewInitDirective } from '../../../shared/directives/after-view-init.directive';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';
import {
	SubmissionFeedbackComponent
} from '../../../shared/components/submission-feedback/submission-feedback.component';
import { ScrollerDirective } from '../../../shared/directives/scroller.directive';

import { WizardPageContent } from './wizard-page-content';
import { WizardStepperComponent } from './wizard-stepper/wizard-stepper.component';

@Component({
	selector: 'copo-wizard',
	templateUrl: './wizard.component.html',
	styleUrl: './wizard.component.css',
	imports: [
		ScrollerDirective,
		NgIf,
		SubmissionFeedbackComponent,
		PageHeaderComponent,

		MatButtonModule,
		TranslateModule,
		WizardStepperComponent,
		LoadingSpinnerComponent,
		RouterOutlet,
		AfterViewInitDirective,
	],
})
export class WizardComponent implements OnInit, OnDestroy {
	@ViewChild('scroller', { read: CdkScrollable }) scrollBar?: CdkScrollable;
	pageTitle: string;
	originView!: string | null;
	pageTitleTranslationCode = 'fastlane.prototyperequest.request';
	isLoading = true;
	previousPage: string | undefined;
	pageContent: WizardPageContent | undefined;
	readonly readOnlyUser?: boolean;
	constructor(
		readonly locationService: LocationService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		readonly location: Location,

		private readonly dialog: MatDialog,
		readonly fastlaneWizardService: FastLaneWizardService,
		readonly errorService: ErrorService,
		private readonly translationService: TranslationService,
		private readonly userService: UserService,
		readonly fastlaneService: FastLaneService
	) {
		this.pageTitle = this.translationService.getTR(this.pageTitleTranslationCode);
		this.previousPage = this.fastlaneWizardService.actionToGoBack();
		if (!this.previousPage) {
			this.previousPage = this.fastlaneWizardService.actionRetrievePreviousPage();
		}
		this.readOnlyUser = this.userService.user.readOnly;
	}

	ngOnInit(): void {
		this.isLoading = false;
	}

	ngOnDestroy(): void {
		this.isLoading = false;
	}

	async onStepClicked(event: StepClickedEvent) {
		if (event.stepIndex === 0) {
			await this.router.navigate(['/fastlane/wizard'], { relativeTo: this.route });
		}
	}

	getCurrentStatus(): string {
		return this.router.url;
	}

	goToBackView() {
		if (this.fastlaneWizardService.getCurrentRoute().endsWith('wizard/mainData')) {
			if (this.previousPage !== undefined) {
				location.href = this.previousPage;
			} else if (this.fastlaneWizardService.getNavigationCount() > 0) {
				this.locationService.go(-this.fastlaneWizardService.getNavigationCount(), this.previousPage);
			} else {
				this.locationService.go(-1, this.previousPage);
			}
		} else {
			location.href = '/home';
		}
	}

	async goNextStep() {
		this.fastlaneService.errMsg = '';
		if (!this.pageContent || this.pageContent.checkError()) {
			return;
		}

		if (this.pageContent.stepIndex === FastLaneWizardStep.MAINDATA) {
			try {
				await this.updateSettingFastlane(this.fastlaneWizardService.getStateFastLaneForm());
			} catch (error) {
				console.error('error while updating the settings', error);
			}
		}

		await this.fastlaneWizardService.actionGoNextStep();
	}

	onActivate(pageContent: WizardPageContent) {
		this.pageContent = pageContent;
		if (!this.scrollBar) {
			return;
		}
		const scrollPosition = this.fastlaneWizardService.getState().scrollPositionByStep[this.pageContent.stepIndex];
		this.scrollBar.scrollTo({ top: scrollPosition });
	}

	onDeactivate() {
		if (!this.pageContent || !this.scrollBar) {
			return;
		}
		this.pageContent = undefined;
	}

	isButtonNextVisible() {
		return !this.isLoading && this.fastlaneWizardService.getState().stepIndex < FastLaneWizardStep.SUMMARY;
	}

	isSummaryStep() {
		return this.fastlaneWizardService.getState().stepIndex === FastLaneWizardStep.SUMMARY;
	}

	async evtSubmitOrder() {
		const sub = this.dialog.open<SubmitDialogComponent, undefined, number>(
			SubmitDialogComponent,
			{ panelClass: 'copo-offer-dialog', disableClose: true }
		);
		this.errorService.resetErrorMsg();
		sub.componentInstance.progressMessage = this.translationService.getTR('progress.message.fastlane');
		sub.componentInstance.waitMessage = this.translationService.getTR('wait.message.fastlane');
		this.isLoading = true;
		await this.fastlaneWizardService.actionSubmitOrder();
		sub.close();
		this.isLoading = false;
	}

	private async updateSettingFastlane(formFastLane: FormFastLane) {
		const fastLaneMainContent = {
			currency: <string>formFastLane.getFormCurrency().value,
			soldTo: <string>formFastLane.getFormSoldTo().value
		};
		const fastLaneUserSettings = this.userService.user.getSettings('fastlane_main');
		if (
			fastLaneUserSettings
			&& this.isSameSettingFastlane(fastLaneMainContent, fastLaneUserSettings.value.fastLaneMainContent)
		) {
			return false;
		}
		if (fastLaneUserSettings) {
			fastLaneUserSettings.value = {
				version: 1,
				fastLaneMainContent,
			};
		} else {
			this.userService.user.settings.push({
				key: 'fastlane_main',
				value: {
					version: 1,
					fastLaneMainContent,
				},
			});
		}
		await this.userService.updateSettings();
		return this.userService.updateUserData();
	}

	private isSameSettingFastlane(
		fastLaneNewSettings: FastLaneMainContent,
		fastLanePreviousSettings: FastLaneMainContent
	) {
		return JSON.stringify(fastLaneNewSettings) === JSON.stringify(fastLanePreviousSettings);
	}
}
